import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { captureError, initSentry } from '@aosenuma/lib-supabase';
import { ErrorBoundary } from '@aosenuma/ui';
import { App } from './App';

import '@aosenuma/ui/styles/tokens.css';
import '@aosenuma/ui/styles/globals.css';
import '@aosenuma/ui/styles/pagination.css';
import '@aosenuma/ui/styles/error-boundary.css';
import '@aosenuma/sig-viewer/styles/toolbar.css';
import './styles/dashboard.css';

// Sentry: cliente Elawan ve PII; cualquier error en su pantalla debe quedar
// trazado para forense + soporte. No-op si VITE_PUBLIC_SENTRY_DSN falta.
void initSentry({ app: 'dashboard' });

// Service Worker: auto-update SIN reload manual. La PWA (registerType:
// 'autoUpdate') cacheaba el bundle viejo y el usuario veía la versión anterior
// hasta vaciar caché. Ahora:
//   (a) forzamos chequeo de updates periódico + al recuperar foco/conexión,
//       para detectar deploys nuevos en pestañas ya abiertas;
//   (b) cuando el SW nuevo toma control (skipWaiting+clientsClaim de
//       autoUpdate) recargamos UNA vez para servir el bundle nuevo.
if ('serviceWorker' in navigator) {
  const hadController = !!navigator.serviceWorker.controller;
  let reloaded = false;
  // No recargar durante el callback de auth (/auth/* o URL con ?code /
  // #access_token): AuthCallback procesa esos params → setSession; un reload
  // ahí los pierde y rompe el login (pantalla en negro). Si no es seguro,
  // diferimos — el SW nuevo ya quedó activo y el código nuevo entra en la
  // próxima navegación natural.
  const safeToReload = () => {
    const { pathname, search, hash } = window.location;
    if (pathname.startsWith('/auth')) return false;
    if (/[?&]code=/.test(search)) return false;
    if (/access_token|refresh_token|type=(recovery|magiclink|signup|invite)/.test(hash)) return false;
    return true;
  };
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Solo en una ACTUALIZACIÓN real (ya existía un SW), una vez, y nunca
    // durante el flujo de auth.
    if (reloaded || !hadController || !safeToReload()) return;
    reloaded = true;
    window.location.reload();
  });
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisteredSW(_swUrl, reg) {
        if (!reg) return;
        const check = () => { void reg.update().catch(() => undefined); };
        window.setInterval(check, 30 * 60 * 1000); // cada 30 min
        window.addEventListener('focus', check);
        window.addEventListener('online', check);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
      },
    });
  }).catch(() => { /* dev mode: sin SW */ });
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary
      app="dashboard"
      onError={(err, info) => {
        void captureError(err, { componentStack: info.componentStack ?? null, app: 'dashboard' });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
